import { unifiedClient } from '@/lib/unified-fetch';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';

const queryKey = ['skins'];

/**
 * Interface for skin data returned from API
 */
export interface SkinData {
  id: number;
  heroId: number;
  name: string | null;
  tag: string | null;
  type: string | null;
}

/**
 * Options for searching skins
 */
export interface UseSkinsSearchOptions {
  filters?: Array<{
    field: "id" | "name";
    op: "eq" | "ne" | "lte" | "gte" | "lt" | "gt" | "like" | "iLike" | "in" | "overlap" | "contains";
    value: string | number | string[] | number[];
  }>;
  page?: number;
  pageSize?: number;
}

/**
 * Hook to search skins using the unified client
 */
export const useSkinsSearch = (
  options: UseSkinsSearchOptions = {}, 
  queryOptions?: Partial<UseQueryOptions>
) => {
  const {
    filters = [],
    page = 1,
    pageSize = 100
  } = options;

  return useQuery({
    queryKey: [queryKey, { filters, page, pageSize }],
    queryFn: async (): Promise<SkinData[]> => {
      const response = await unifiedClient.app.POST('/skins/search', {
        body: {
          filters,
          page,
          pageSize,
        }
      });

      const { data, error } = response;

      if (error) {
        throw new Error('Failed to fetch skins');
      }

      return data?.items || [];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime)
    ...queryOptions,
  });
};

/**
 * Function to fetch skin names by IDs (for use outside React components)
 */
export const fetchSkinsByIds = async (ids: number[]): Promise<SkinData[]> => {
  if (!ids || ids.length === 0) {
    return [];
  }

  const response = await unifiedClient.app.POST('/skins/search', {
    body: {
      filters: [
        {
          field: "id",
          op: "in",
          value: ids
        }
      ],
      page: 1,
      pageSize: Math.max(100, ids.length) // Ensure we can fetch all requested IDs
    }
  });

  const { data, error } = response;

  if (error) {
    throw new Error('Failed to fetch skins by IDs');
  }

  return data?.items || [];
};

/**
 * Hook to fetch skins by specific IDs
 */
export const useSkinsByIds = (
  ids: number[],
  queryOptions?: Partial<UseQueryOptions>
) => {
  return useSkinsSearch(
    {
      filters: ids.length > 0 ? [
        {
          field: "id",
          op: "in",
          value: ids
        }
      ] : [],
      page: 1,
      pageSize: Math.max(100, ids.length)
    },
    {
      enabled: ids.length > 0,
      ...queryOptions,
    }
  );
};
